import { Delete, Visibility } from '@mui/icons-material';
import { Button } from '@mui/material';
import api from 'lib/trpcClient';
import {
  createAddInvestmentTransferRoute,
  createInvestmentViewLink,
} from 'adminConstants/routes';
import AlertDialog from 'components/AlertDialog';
import Heading from 'components/Heading';
import { Column } from 'components/Layout';
import PageHeader from 'components/PageHeader';
import ResponsiveActions from 'components/PageHeader/ResponsiveActions';
import Spacing from 'components/Spacing';
import { EIS3Download } from 'further-ui/components/SVGIcons';
import Table, { FooterCell, FooterRow, TableActions } from 'components/Table';
import TooltipTotalValue from 'components/TooltipTotalValue';
import { useDisclosure, usePagination } from 'further-ui/hooks';
import {
  dateToLabel,
  numberToCurrencyString,
  renderMultiple,
} from 'further-ui/utils';
import { useDownloadByInvestmentId } from 'hooks/data/eisWizard/useDownloadEisCertificatePdfs';
import { useInvestments } from 'hooks/data/investment/useInvestments';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useGetRole } from 'hooks/ui/useGetRole';
import { sumBy } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type Props = {
  investorId: string;
};

const InvestmentsTable: React.FC<Props> = ({ investorId }) => {
  const pagination = usePagination();
  const deleteDisclosure = useDisclosure<{ _id: string }>();
  const deleteMutation = api.investment.delete.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Investment deleted successfully.',
    }),
  );
  const { firmId, isSuperAdmin } = useGetRole();
  const [canTransfer, canDeleteInvestment, canDownloadSeis] = useHasPermissions(
    [
      RolePermission.TransferInvestment,
      RolePermission.DeleteInvestment,
      RolePermission.DownloadSeis,
    ],
  );

  const downloadEisCerts = useDownloadByInvestmentId();
  const investments = useInvestments({
    params: {
      investorId,
      firmId,
      page: pagination.page.toString(),
      perPage: pagination.rowsPerPage.toString(),
      sortType: 'desc',
      keyName: 'investmentDate',
    },
  });

  const downloadAllEISCertificates = (investment) => {
    downloadEisCerts.download({
      investmentId: investment._id,
      fullName: investment.fullName,
      trancheName: `${investment.firmId.firmName}-${investment.fundId.fundName}`,
    });
  };

  const columns = [
    ...(isSuperAdmin
      ? [
          {
            label: 'Firm',
            key: 'firmName',
            sort: false,
            render: (elm) => elm?.firmId?.firmName,
          },
        ]
      : []),
    {
      label: 'Tranche',
      key: 'fundName',
      sort: false,
      render: (elm) => elm?.fundId?.fundName,
    },
    {
      label: 'External subscription ID',
      key: 'externalInvestmentId',
      sort: false,
      render: (elm) => elm.externalInvestmentId,
    },
    {
      label: 'Subscription date',
      key: 'investmentDate',
      sort: false,
      render: (elm) => dateToLabel(elm.investmentDate),
    },
    {
      label: 'Subscription amount',
      key: 'investmentAmount',
      sort: false,
      render: (elm) => numberToCurrencyString(elm?.investmentAmount),
    },
    {
      label: 'Uninvested capital',
      key: 'uninvestedCapital',
      sort: false,
      render: (elm) =>
        numberToCurrencyString(
          elm?.investmentBalance?.deploymentSummary?.undeployedCommitment,
        ),
    },
    {
      label: <TooltipTotalValue />,
      key: 'totalCurrentValue',
      sort: false,
      render: (elm) =>
        numberToCurrencyString(elm?.investmentBalance?.investmentValue),
    },
    {
      label: 'Net realised value',
      key: 'netRealisedValue',
      sort: false,
      render: (elm) =>
        numberToCurrencyString(elm?.investmentBalance?.realisedValue?.total),
    },
    {
      label: 'Multiple on investment',
      key: 'multipleOnInvestment',
      sort: false,
      render: (elm) =>
        renderMultiple(
          elm?.investmentBalance?.valueChangesOnInitialInvestment
            ?.multipleOnInvestment,
        ),
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              link: createInvestmentViewLink(elm._id),
              color: 'primary',
              disabled: downloadEisCerts.isLoading,
              icon: Visibility,
              label: 'View',
            },
            {
              visible: canDownloadSeis && elm?.isAllocationExists,
              label: 'Download all EIS certificates',
              onClick: () => downloadAllEISCertificates(elm),
              icon: EIS3Download,
            },
            {
              onClick: () => deleteDisclosure.stageAction(elm),
              label: 'Delete',
              visible: canDeleteInvestment,
              icon: Delete,
              color: 'error',
            },
          ]}
        />
      ),
    },
  ];

  const handleConfirmDelete = async () => {
    const record = deleteDisclosure.actionProps;
    deleteMutation.mutate({ investmentId: record._id });
    deleteDisclosure.onClose();
  };

  return (
    <Spacing>
      <PageHeader>
        <Heading variant="h2" noMargin>
          Subscriptions
        </Heading>
        <ResponsiveActions>
          {canTransfer && (
            <Link to={createAddInvestmentTransferRoute(investorId)}>
              <Button color="primary" variant="contained" size="small">
                Transfer subscriptions
              </Button>
            </Link>
          )}
        </ResponsiveActions>
      </PageHeader>
      <Table
        pagination={true}
        columns={columns}
        tablebody={investments.data?.result || []}
        variant="nohover"
        emptyMessage={'No subscriptions created for this investor yet.'}
        onPageChange={pagination.handleChangePage}
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        page={pagination.page}
        loading={investments.isFetching}
        rowsPerPage={pagination.rowsPerPage}
        count={investments.data?.total ?? 0}
        TableFooter={
          <tfoot>
            <FooterRow>
              <FooterCell colSpan={isSuperAdmin ? 4 : 3} />
              <FooterCell>
                {numberToCurrencyString(
                  sumBy(investments.data?.result, 'investmentAmount'),
                )}
              </FooterCell>
              <FooterCell>
                {numberToCurrencyString(
                  sumBy(
                    investments.data?.result,
                    'investmentBalance.deploymentSummary.undeployedCommitment',
                  ),
                )}
              </FooterCell>
              <FooterCell>
                {numberToCurrencyString(
                  sumBy(
                    investments.data?.result,
                    'investmentBalance.investmentValue',
                  ),
                )}
              </FooterCell>
              <FooterCell>
                {numberToCurrencyString(
                  sumBy(
                    investments.data?.result,
                    'investmentBalance.realisedValue.total',
                  ),
                )}
              </FooterCell>
              <FooterCell colSpan={2} />
            </FooterRow>
          </tfoot>
        }
      />

      <AlertDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        onConfirm={handleConfirmDelete}
        title={
          'This cannot be undone and all information relating to the subscription will be lost.'
        }
        content={
          <Column spacing="xs">
            <p>
              Any money transferred to you must be refunded to the investor
              before the subscription is deleted.
            </p>
            <p>
              Any money that was transferred from the investor's cash balance to
              pay for this subscription will be returned to their cash balance.
            </p>
          </Column>
        }
        confirmBtnProps={{ color: 'secondary' }}
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Delete subscription',
        }}
      />
    </Spacing>
  );
};

export default InvestmentsTable;
